import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import Widget from '../../../components/widget/widget.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ActiveBlock from '../../../components/active-block/active-block.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import {
  DEAL_CONTACTS_GROUP_ITEM_EDIT, DEAL_CONTACTS_GROUP_ITEM_DELETE, DEAL_CONTACTS_GROUP_ITEM_CREATE,
  DEAL_CONTACTS_ITEM_CREATE, DEAL_CONTACTS_ITEM_EDIT, DEAL_CONTACTS_LIST_VIEW, DEAL_CONTACTS_ITEM_DELETE,
} from '../../../constants/entity/permissions';

const DealContacts: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deal Contacts';
  const COLUMNS_TOTAL: number = 2;
  const WIDGET_NAME: string = 'widget-deal-contacts';

  const {
    isEditMode,
    isNewDeal,
    dealContactsData,
    dealContactNewItems,
    dealContactNewGroupItems,
    onAddNewItem,
    onDeleteClick,
    onDeleteGroupClick,
    onAddNewGroupItem,
    onEditModeButtonClick,
  } = props;

  const [dealContactItems, setDealContactItems]: [Record<string, Record<string, DealContact>> | null, Function]
    = useState<StateValueDefault>(null);
  const [dealContactGroupItems, setDealContactGroupItems]: [Record<string, DealContactGroup> | null, Function]
    = useState<StateValueDefault>(null);

  const dealContactsItemCreatePermissions: string[] = useMemo((): string[] => [DEAL_CONTACTS_ITEM_CREATE], []);
  const dealContactsItemEditPermissions: string[] = useMemo((): string[] => [DEAL_CONTACTS_ITEM_EDIT], []);
  const dealContactsItemDeletePermissions: string[] = useMemo((): string[] => [DEAL_CONTACTS_ITEM_DELETE], []);
  const dealContactsListViewPermissions: string[] = useMemo((): string[] => [DEAL_CONTACTS_LIST_VIEW], []);
  const dealContactsGroupItemCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_CONTACTS_GROUP_ITEM_CREATE,
  ], []);
  const dealContactsGroupItemEditPermissions: string[] = useMemo((): string[] => [
    DEAL_CONTACTS_GROUP_ITEM_EDIT,
  ], []);
  const dealContactsGroupItemDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_CONTACTS_GROUP_ITEM_DELETE,
  ], []);

  const dealContactGroupsTotal: number = [...((dealContactGroupItems && Object.keys(dealContactGroupItems)) || []),
    ...(dealContactNewGroupItems || [])].length;
  const dealContactItemsTotal: number = [...((dealContactGroupItems && Object.values(dealContactGroupItems)) || []),
    ...(dealContactNewGroupItems || [])].map((dealContactGroupItem: DealContactGroup): DealContact[] => (
    dealContactGroupItem.contacts
  )).flat().length;
  const formPrefix: string = 'dealData.dealContactsData';
  const isEmpty: boolean = !isEditMode && !isNewDeal && (!dealContactsData || !dealContactsData.length);

  const addNewItem: Function = useCallback((dealContactGroupId: number): void => {
    if (onAddNewItem) {
      onAddNewItem(dealContactGroupId, {
        id: StructureService.getNewItemId(
          dealContactNewItems[dealContactGroupId] || [],
          [...((dealContactGroupItems && Object.values(dealContactGroupItems)) || []),
            ...(dealContactNewGroupItems || [])],
          'contacts',
        ),
        name: '',
        value: '',
      });
    }
  }, [dealContactGroupItems, dealContactNewGroupItems, dealContactNewItems]);

  const deleteClick: Function = useCallback((dealContactItemId: number, dealContactGroupId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealContactItemId, dealContactGroupId);
    }

    if (dealContactItems && dealContactItems[dealContactGroupId]) {
      const newDealContactItems: Record<string, Record<string, DealContact>> = { ...dealContactItems };
      delete newDealContactItems[dealContactGroupId][dealContactItemId];
      setDealContactItems(newDealContactItems);
    }
  }, [dealContactItems, dealContactNewItems]);

  const addNewGroupItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewGroupItem) {
      onAddNewGroupItem({
        id: StructureService.getNewItemId(
          dealContactNewGroupItems,
          (dealContactGroupItems && Object.values(dealContactGroupItems)) || [],
        ),
        name: '',
        contacts: [],
      });
    }
  }, [dealContactGroupItems, dealContactNewGroupItems]);

  const deleteGroupClick: Function = useCallback((dealContactGroupId: number): void => {
    if (onDeleteGroupClick) {
      onDeleteGroupClick(dealContactGroupId);
    }

    if (dealContactGroupItems) {
      const newDealContactGroupItems: Record<string, DealContactGroup> = { ...dealContactGroupItems };
      delete newDealContactGroupItems[dealContactGroupId];
      setDealContactGroupItems(newDealContactGroupItems);
    }
  }, [dealContactGroupItems, dealContactNewGroupItems]);

  useEffect((): void => {
    if (dealContactsData) {
      const initialDealContactGroupItems: Record<string, DealContactGroup> = {};

      dealContactsData.forEach((dealContactGroupItem: DealContactGroup): void => {
        initialDealContactGroupItems[dealContactGroupItem.id] = { ...dealContactGroupItem };
      });

      setDealContactGroupItems(initialDealContactGroupItems);
    }
  }, [dealContactsData]);

  useEffect((): void => {
    if ((dealContactGroupItems && Object.keys(dealContactGroupItems).length)
      || (dealContactNewGroupItems && dealContactNewGroupItems.length)) {
      const initialDealContactItems: Record<string, Record<string, DealContact>> = {};

      if (dealContactGroupItems) {
        Object.values(dealContactGroupItems).forEach((dealContactGroupItem: DealContactGroup): void => {
          if (!initialDealContactItems[dealContactGroupItem.id]) {
            initialDealContactItems[dealContactGroupItem.id] = {};
          }

          if (dealContactGroupItem.contacts && dealContactGroupItem.contacts.length) {
            dealContactGroupItem.contacts.forEach((dealContactItem: DealContact): void => {
              initialDealContactItems[dealContactGroupItem.id][dealContactItem.id] = { ...dealContactItem };
            });
          }
        });
      }

      setDealContactItems(initialDealContactItems);
    }
  }, [dealContactGroupsTotal, dealContactItemsTotal]);

  return (
    <CheckAccess permissions={dealContactsItemEditPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        !isEmpty && (
          <Widget
            isFullWidth
            isFullHeight
            isEmpty={isEmpty}
            isEditMode={isEditMode}
            withEditMode={!isNewDeal}
            editPermissions={dealContactsItemEditPermissions}
            widgetName={WIDGET_NAME}
            defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
            onEditModeButtonClick={onEditModeButtonClick}
          >
            {
              [...((dealContactGroupItems && Object.values(dealContactGroupItems)) || []),
                ...(dealContactNewGroupItems || [])]
                .map((dealContactGroupItem: DealContactGroup): ReactElement => {
                  const isNewGroup: boolean = dealContactNewGroupItems
                    && !!dealContactNewGroupItems.length && !!dealContactNewGroupItems.find((
                      dealContactNewGroupItem: DealContactGroup,
                    ): boolean => (
                      dealContactNewGroupItem.id === dealContactGroupItem.id
                    ));
                  const formKeyDealContactGroupItem: string = FormService.getIndexedFormFieldKey(
                    dealContactGroupItem.id,
                  );

                  return (
                    !isEditMode && !isNewDeal ? (
                      <CheckAccess key={dealContactGroupItem.id} permissions={dealContactsListViewPermissions}>
                        <ExpansionGroup isFixed isExpanded headerComponent={dealContactGroupItem.name}>
                          <Table>
                            <TableBody isPlain withZebraRows withHeaderColumn>
                              {
                                (dealContactGroupItem.contacts || []).map((
                                  dealContactItem: DealContact,
                                ): ReactElement => (
                                  <TableRow key={dealContactItem.id}>
                                    <TableCell>
                                      {dealContactItem.name}
                                    </TableCell>
                                    <TableCell>
                                      {dealContactItem.value}
                                    </TableCell>
                                  </TableRow>
                                ))
                              }
                            </TableBody>
                          </Table>
                        </ExpansionGroup>
                      </CheckAccess>
                    ) : (
                      <FlexContainer isColumn key={dealContactGroupItem.id}>
                        <CheckAccess
                          isEnabled={isEditMode || isNewDeal}
                          permissions={dealContactsGroupItemEditPermissions}
                        >
                          <ActiveBlock isActive={isNewGroup && !isNewDeal}>
                            <FlexContainer isFullWidth>
                              <PaddedFlexContainer isFullWidth>
                                <FormFieldText
                                  isHidden
                                  name={`${formPrefix}[${formKeyDealContactGroupItem}].id`}
                                />
                                <FormFieldText
                                  name={`${formPrefix}[${formKeyDealContactGroupItem}].name`}
                                  placeholder="Group Name"
                                />
                              </PaddedFlexContainer>
                            </FlexContainer>
                            <CheckAccess permissions={dealContactsGroupItemDeletePermissions}>
                              <FlexContainer isEnd>
                                <PaddedFlexContainer isFullWidth>
                                  <Tooltip title="Delete">
                                    <ActionButton
                                      isInline
                                      withSmallPadding
                                      onClick={(): void => {
                                        deleteGroupClick(dealContactGroupItem.id);
                                      }}
                                    >
                                      <RemoveIcon />
                                    </ActionButton>
                                  </Tooltip>
                                </PaddedFlexContainer>
                              </FlexContainer>
                            </CheckAccess>
                          </ActiveBlock>
                        </CheckAccess>
                        <FlexContainer isFullWidth>
                          {
                            dealContactItems && (
                              <CheckAccess permissions={dealContactsItemEditPermissions}>
                                <Table>
                                  <TableBody isPlain withTopBorder withZebraRows withHeaderColumn>
                                    {
                                      [
                                        ...((dealContactItems[dealContactGroupItem.id]
                                          && Object.values(dealContactItems[dealContactGroupItem.id])) || []),
                                        ...(dealContactNewItems[dealContactGroupItem.id] || []),
                                      ].map((dealContactItem: DealContact): ReactElement => {
                                        const isNewItem: boolean = dealContactNewItems[dealContactGroupItem.id]
                                          && !!dealContactNewItems[dealContactGroupItem.id].length
                                          && !!dealContactNewItems[dealContactGroupItem.id].find((
                                            dealContactNewItem: DealContact,
                                          ): boolean => (
                                            dealContactNewItem.id === dealContactItem.id
                                          ));
                                        const formKeyDealContactItem: string = FormService.getIndexedFormFieldKey(
                                          dealContactItem.id as number,
                                        );

                                        return (
                                          <TableRow
                                            isActive={(isNewItem || isNewGroup) && !isNewDeal}
                                            key={dealContactItem.id}
                                          >
                                            <TableCell>
                                              <FormFieldText
                                                isHidden
                                                name={`${formPrefix}[${formKeyDealContactGroupItem}].`
                                                  + `contacts[${formKeyDealContactItem}].id`}
                                              />
                                              <FormFieldText
                                                name={`${formPrefix}[${formKeyDealContactGroupItem}].`
                                                  + `contacts[${formKeyDealContactItem}].name`}
                                                placeholder="Name"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <FlexContainer>
                                                <FormFieldText
                                                  name={`${formPrefix}[${formKeyDealContactGroupItem}].`
                                                    + `contacts[${formKeyDealContactItem}].value`}
                                                  placeholder="Value"
                                                />
                                                <CheckAccess permissions={dealContactsItemDeletePermissions}>
                                                  <div>
                                                    <Tooltip title="Delete">
                                                      <ActionButton
                                                        isInline
                                                        withSmallPadding
                                                        onClick={(): void => {
                                                          deleteClick(dealContactItem.id, dealContactGroupItem.id);
                                                        }}
                                                      >
                                                        <RemoveIcon />
                                                      </ActionButton>
                                                    </Tooltip>
                                                  </div>
                                                </CheckAccess>
                                              </FlexContainer>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })
                                    }
                                    <CheckAccess permissions={dealContactsItemCreatePermissions}>
                                      <TableRowAddNewItem
                                        label="Add New Contact"
                                        colSpan={COLUMNS_TOTAL}
                                        onClick={(): void => {
                                          addNewItem(dealContactGroupItem.id);
                                        }}
                                      />
                                    </CheckAccess>
                                  </TableBody>
                                </Table>
                              </CheckAccess>
                            )
                          }
                        </FlexContainer>
                      </FlexContainer>
                    )
                  );
                })
            }
            {
              (isEditMode || isNewDeal) && (
                <CheckAccess permissions={dealContactsGroupItemCreatePermissions}>
                  <Card isContainer isSelectable withSmallPadding onClick={addNewGroupItem}>
                    <ActionButtonNewItem withNoHighlight label="Add New Group" />
                  </Card>
                </CheckAccess>
              )
            }
          </Widget>
        )
      }
    </CheckAccess>
  );
});

export default DealContacts;
